'use client';

import { type JSX, useEffect, useRef } from 'react';
import { usePublicConfigurationContext } from 'runtime-configuration';

import { useGrecaptchaReady } from './useGrecaptchaReady';

export interface RecaptchaV2CheckboxProps {
    onChange: (token: string | null) => void;
    onError?: () => void;
    theme?: ReCaptchaV2.Theme;
    size?: ReCaptchaV2.Size;
}

/**
 * Renders the reCAPTCHA v2 "I'm not a robot" checkbox and reports its token via onChange.
 * Returns null when recaptcha is disabled (local dev / synthetics); the server verifier
 * short-circuits to true in that case, so no token is required.
 */
export function RecaptchaV2Checkbox({ onChange, onError, theme, size }: RecaptchaV2CheckboxProps): JSX.Element | null {
    const containerRef = useRef<HTMLDivElement>(null);
    const { recaptchaV2SiteKey, recaptchaDisabled } = usePublicConfigurationContext();
    const isReady = useGrecaptchaReady();

    const onChangeRef = useRef(onChange);
    const onErrorRef = useRef(onError);
    useEffect(() => {
        onChangeRef.current = onChange;
        onErrorRef.current = onError;
    });

    useEffect(() => {
        const container = containerRef.current;
        // `isReady` (from useGrecaptchaReady) flips once the SDK has loaded. Gating on it rather
        // than reading `grecaptcha` directly closes the "afterInteractive" load-order race: when the
        // script loads after this component mounts, the readiness update re-runs this effect and the
        // widget renders then.
        if (recaptchaDisabled || !container || !isReady) {
            return;
        }
        let widgetId: number | undefined;
        grecaptcha.ready(() => {
            widgetId = grecaptcha.render(container, {
                sitekey: recaptchaV2SiteKey,
                theme,
                size,
                callback: (token: string) => onChangeRef.current(token),
                'expired-callback': () => onChangeRef.current(null),
                'error-callback': () => onErrorRef.current?.(),
            });
        });
        return () => {
            if (widgetId !== undefined) {
                grecaptcha.reset(widgetId);
            }
        };
    }, [recaptchaV2SiteKey, recaptchaDisabled, theme, size, isReady]);

    if (recaptchaDisabled) {
        return null;
    }
    return <div ref={containerRef} />;
}
